import { memo } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, IdCard, Calendar, Eye, Edit, CheckCircle2, XCircle } from 'lucide-react';
import { formatPhone, formatDate, getStatusColor } from '../../../utils/clientHelpers';

const ClientTableRow = memo(({ client, index, onView, onEdit, onVerify }) => {
  return (
    <motion.tr
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ delay: index * 0.05 }}
      className="transition-colors duration-150 hover:bg-emerald-50/50"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full shadow-md">
            <span className="text-sm font-bold text-white">
              {client.firstName?.charAt(0) || ''}{client.lastName?.charAt(0) || ''}
            </span>
          </div>
          <div className="ml-4">
            <div className="text-sm font-semibold text-gray-900">
              {client.firstName} {client.lastName}
            </div>
            {client.gender && (
              <div className="text-xs text-gray-500 mt-0.5 capitalize">
                {client.gender}
              </div>
            )}
          </div>
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="space-y-1.5">
          <div className="flex items-center text-sm text-gray-900">
            <Mail className="mr-2 w-4 h-4 text-emerald-500" />
            <span className="truncate max-w-[200px]">{client.email || 'N/A'}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <Phone className="mr-2 w-4 h-4 text-emerald-500" />
            {formatPhone(client.phone)}
          </div>
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="space-y-1.5">
          <div className="flex items-center text-sm text-gray-900">
            <MapPin className="mr-2 w-4 h-4 text-emerald-500" />
            {client.region || 'N/A'}
          </div>
          {client.location && (
            <div className="ml-6 text-sm text-gray-600">
              {client.location}
            </div>
          )}
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="space-y-1.5">
          <div className="flex items-center text-sm font-medium text-gray-900">
            <IdCard className="mr-2 w-4 h-4 text-emerald-500" />
            {client.idType}
          </div>
          {client.cardNumber && (
            <div className="ml-6 text-xs text-gray-600 font-mono">
              {client.cardNumber}
            </div>
          )}
          {client.issueDate && (
            <div className="ml-6 text-xs text-gray-500 flex items-center">
              <Calendar className="mr-1 w-3 h-3" />
              Issued: {formatDate(client.issueDate)}
            </div>
          )}
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full border-2 ${getStatusColor(client.status)}`}>
          {client.status || 'Active'}
        </span>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onView(client)}
            className="p-2 text-gray-400 rounded-lg transition-colors duration-150 hover:text-blue-600 hover:bg-blue-50"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.1, rotate: -5 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onEdit(client)}
            className="p-2 text-gray-400 rounded-lg transition-colors duration-150 hover:text-emerald-600 hover:bg-emerald-50"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onVerify(client)}
            className={`p-2 rounded-lg transition-colors duration-150 ${
              client.status?.toLowerCase() === 'active' || client.emailVerified || client.phoneVerified
                ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
            title={client.status?.toLowerCase() === 'active' || client.emailVerified || client.phoneVerified ? 'Unverify' : 'Verify'}
          >
            {client.status?.toLowerCase() === 'active' || client.emailVerified || client.phoneVerified ? (
              <XCircle className="w-4 h-4" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
          </motion.button>
        </div>
      </td>
    </motion.tr>
  );
});

ClientTableRow.displayName = 'ClientTableRow';

export default ClientTableRow;
